import styled from "styled-components";
import React, { Component, useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import type { Transaction } from "../../../redux/modules/transaction";
import { fetchTransactions } from "../../../redux/modules/transaction";
import type { Settlement } from "../../../redux/modules/settlement";
import { Button, Dimmer, Loader, Modal, Segment } from "semantic-ui-react";
import dayjs from "dayjs";
import { PrintReceiptButton } from "../../../components/billing/print-receipt-button";
import { Print } from "react-easy-print";
import { isAndroid } from "react-device-detect";
import { Currency } from "../../../containers/common/currency";
import { formatDate } from "../../../helpers/date";
import { SettlementCancelTransactionForm } from "./settlement-cancel-transaction";
import { RoleRequired } from "../../auth/role-required";
import { permissions } from "../../../constant/permissions";
import { t } from "../../../constant/translations";
import { BGN, EUR } from "../../../helpers/currency";

type BreakdownListProps = {
  transactions: Transaction[],
  settlement: Settlement,
};

const BreakdownContainer = styled.div`
  ul {
    list-style: none;
  }
`;

const TransactionContainer = styled.div`
  > div {
    margin: 10px 0;
  }
`;

const BoldText = styled.span`
  font-weight: bold;
`;

function TransactionItem(props) {
  const transaction: Transaction = props.transaction;
  const charge = transaction.charge;
  const chargeDetails = charge.details;
  return (
    <Segment>
      <RoleRequired
        admin={true}
        permission={permissions.billingSettlementDelete}
      >
        <SettlementCancelTransactionForm
          entity={transaction}
          settlementId={props.settlementId}
        />
      </RoleRequired>
      {t("resources.charge_to")} {charge.fund} {t("charge.from_date")}{" "}
      {formatDate(charge.charge_date)}
      <br />
      {chargeDetails.map((details, idx) => {
        return (
          <div key={idx}>
            <span id={idx}>
              {details.model} -{" "}
              <BoldText>
                <Currency amount={details.amount} />
              </BoldText>
            </span>
            <br />
          </div>
        );
      })}
      <br />
      <BoldText>
        {" "}
        {t("settlement.paid")}: <Currency amount={transaction.amount} />
      </BoldText>
    </Segment>
  );
}

const BreakdownList = (props: BreakdownListProps) => {
  const settlement = props.settlement;
  const transactions = props.transactions
    .filter((t) => t.charge !== null)
    .sort((t1, t2) => {
      return dayjs(t1.charge.charge_date) - dayjs(t2.charge.charge_date);
    });
  return (
    <BreakdownContainer>
      <div>
        <h4>{t("settlement.payment_info")}</h4>

        <ul>
          <li>
            {t("settlement.payment_number")}: {settlement.id}{" "}
          </li>
          <li>
            {t("labels.date")}: {settlement.settle_date}{" "}
          </li>
          <li>
            {t("resources.estate_capital")}: {settlement.block_details.name} -{" "}
            {settlement.estate_details.name}
          </li>
          <li>
            {t("labels.client_number")}: {settlement.estate_details.vendor_id}
          </li>
        </ul>
        <ul>
          <li>
            {t("labels.deposited_amount")}:{" "}
            <Currency
              amount={settlement.deposit_amount}
              selectedCurrency={BGN}
            />{" "}
            /{" "}
            <Currency
              amount={settlement.deposit_amount}
              selectedCurrency={EUR}
            />
          </li>
          <li>
            {t("titles.duties_paid")}:{" "}
            <Currency
              amount={settlement.settled_amount}
              selectedCurrency={BGN}
            />{" "}
            /{" "}
            <Currency
              amount={settlement.settled_amount}
              selectedCurrency={EUR}
            />
          </li>
          <li>
            {t("labels.prepaid_amount")}:{" "}
            <Currency
              amount={settlement.prepaid_amount}
              selectedCurrency={BGN}
            />{" "}
            /{" "}
            <Currency
              amount={settlement.prepaid_amount}
              selectedCurrency={EUR}
            />
          </li>
        </ul>
      </div>
      {transactions && transactions.length > 0 && (
        <TransactionContainer>
          <h4>{t("titles.duties_paid")}</h4>
          {transactions.map((transaction) => (
            <TransactionItem
              transaction={transaction}
              key={transaction.id}
              settlementId={settlement.id}
            />
          ))}
        </TransactionContainer>
      )}
    </BreakdownContainer>
  );
};

type SettlementBreakdownProps = {
  transactions: ?(Transaction[]),
  settlement: Settlement,
  loading: boolean,
  error: Object,
  fetchTransactions: Function,
  onClose: Function,
};

const SettlementBreakdown = ({
  settlement,
  transactions,
  loading,
  error,
  onClose,
  fetchTransactions,
}: SettlementBreakdownProps) => {
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (settlement?.id) {
      fetchTransactions(
        {
          settlement: settlement.id,
          page_size: 400,
        },
        true
      );
    }
  }, [settlement?.id, fetchTransactions]);

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} closeIcon={true}>
      {loading && (
        <Dimmer active={true} inverted>
          <Loader />
        </Dimmer>
      )}
      <Modal.Header>
        {t("settlement.payment_to")} {settlement.block_details.name} -{" "}
        {settlement.estate_details.name}
      </Modal.Header>
      <Modal.Content>
        <Print name={"settlement"}>
          <BreakdownList transactions={transactions} settlement={settlement} />
        </Print>
      </Modal.Content>
      <Modal.Actions>
        {(isAndroid || process.env.NODE_ENV === "development") && (
          <PrintReceiptButton
            settlement={settlement}
            transactions={transactions}
            // isDev={process.env.NODE_ENV === "development"}
          />
        )}
        <Button onClick={handleClose}>{t("labels.close")}</Button>
      </Modal.Actions>
    </Modal>
  );
};

function mapStateToProps(state) {
  return {
    transactions: state.transaction.entities || [],
    error: state.transaction.error,
    loading: state.transaction.pending,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ fetchTransactions }, dispatch);
}

export const SettlementBreakdownComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(SettlementBreakdown);
